import { useEffect, useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { DOCUMENT_TYPES, useDocumentTypes } from "@/api/documentTypes";
import { useCreateDocumentRequest } from "@/hooks/deals/useDealDetail";
import type { PreApprovalRequestParty } from "@/types/deals";

const PARTIES: PreApprovalRequestParty[] = [
  "Borrower",
  "Seller",
  "Business",
  "Broker",
  "Multiple",
];

function describeApi(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.errors.length) return err.errors.join("; ");
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// =============================================================================
// AddCustomDocumentRequestModal
// =============================================================================

export function AddCustomDocumentRequestModal({
  open,
  dealId,
  onClose,
}: {
  open: boolean;
  dealId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateDocumentRequest(dealId);
  // The DocumentTypeRef catalog is now sourced from the API; the static
  // catalog is the hook's fallback until the fetch returns (or if it fails).
  const { data: documentTypes } = useDocumentTypes();
  const [code, setCode] = useState(documentTypes[0]?.code ?? DOCUMENT_TYPES[0].code);
  const [party, setParty] = useState<PreApprovalRequestParty>("Borrower");
  const [required, setRequired] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode(documentTypes[0]?.code ?? DOCUMENT_TYPES[0].code);
      setParty("Borrower");
      setRequired(true);
      setDueDate("");
      setNotes("");
      setError(null);
    }
  }, [open, documentTypes]);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        documentTypeRefCode: code,
        requestedFromParty: party,
        required,
        dueDate: dueDate || null,
        notes: notes.trim() || null,
      });
      toast.success("Document request added.");
      onClose();
    } catch (err) {
      setError(describeApi(err, "Failed to add request."));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add custom document request"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            Add request
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Document type" required>
          <Select value={code} onChange={(e) => setCode(e.target.value)}>
            {documentTypes.map((d) => (
              <option key={d.code} value={d.code}>
                {d.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField label="Requested from" required>
            <Select
              value={party}
              onChange={(e) => setParty(e.target.value as PreApprovalRequestParty)}
            >
              {PARTIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Due date">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </FormField>
        </div>

        <FormField label="Required?">
          <label className="inline-flex items-center gap-space-2 text-small text-onyx">
            <Input
              type="checkbox"
              className="!w-4 !h-4 !p-0"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Required for certification
          </label>
        </FormField>

        <FormField label="Notes">
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional context for the recipient."
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
